import { ApiError, createApiClient, Result, wrapper } from "@/api/base";
import MasterPriceDO = Modules.BookingExperts.Data.MasterPriceList.MasterPriceDO;
import PriceModelCurveDO = Modules.BookingExperts.Data.PriceModelCurve.PriceModelCurveDO;
import CurveApplicationRangeDO = Modules.BookingExperts.Data.CurveApplicationRange.CurveApplicationRangeDO;
import PriceModelCurvePostDTO = Modules.BookingExperts.Data.PriceModelCurve.PriceModelCurvePostDTO;
import PriceModelCurvePatchDTO = Modules.BookingExperts.Data.PriceModelCurve.PriceModelCurvePatchDTO;
import BexAdministrationDO = Modules.BookingExperts.Data.BexAdministrationDO;
import BexEnvironmentDO = Modules.BookingExperts.Data.BexEnvironmentDO;
import CurveApplicationRangesPutDTO = Modules.BookingExperts.Data.CurveApplicationRange.CurveApplicationRangesPutDTO;
import CopyCurvesPostDTO = Modules.BookingExperts.Data.CurveApplicationRange.CopyCurvesPostDTO;
import PeriodSelectionDTO = App.Data.Explorer.PeriodSelectionDTO;
import OverviewStatsDTO = Modules.BookingExperts.Data.OverviewStatsDTO;
import { LaravelPaginatedCollection } from "@/types/collections";

type ComplexPriceItemDTO =
	Modules.BookingExperts.Data.MasterPriceList.ComplexPriceItemDTO;
type BookingDTO = Modules.BookingExperts.Data.BookingDTO;
import CurveApplicationRangeImpactDTO = Modules.BookingExperts.Data.CurveApplicationRange.CurveApplicationRangeImpactDTO;
import PriceModelCurveBaseDataDTO = Modules.BookingExperts.Data.PriceModelCurve.PriceModelCurveBaseDataDTO;
import ComplexPriceItemCurveApplicationStatsDTO = Modules.BookingExperts.Data.MasterPriceList.ComplexPriceItemCurveApplicationStatsDTO;
import MasterPriceComparisonDO = Modules.BookingExperts.Data.MasterPriceList.MasterPriceComparisonDO;
import PriceUpdateStatsDTO = Modules.BookingExperts.Data.PriceUpdateStatsDTO;
import FailedPriceUpdateDTO = Modules.BookingExperts.Data.FailedPriceUpdateDTO;
import RentableTypeAssetDO = Modules.BookingExperts.Data.RentableTypeAssetDO;
import BexRentableSegmentDO = Modules.BookingExperts.Data.BexRentableSegmentDO;
import MasterPriceListImportDO = Modules.BookingExperts.Data.MasterPriceList.MasterPriceListImportDO;
import MasterPriceListImportListDO = Modules.BookingExperts.Data.MasterPriceList.MasterPriceListImportListDO;
import PriceModelCurveUsageDTO = Modules.BookingExperts.Data.PriceModelCurve.PriceModelCurveUsageDTO;
import MinMaxOffsetBreachedDTO = Modules.BookingExperts.Data.MinMaxOffsetBreachedDTO;
import MinMaxOffsetBreachedDate = Modules.BookingExperts.Data.MinMaxOffsetBreachedDate;

const basePath = "bex";
const baseClient = createApiClient();

interface CurveSearchParams {
	"filter[name]"?: string;
}

interface HistoricCurveSearchParams {
	"filter[date]"?: string;
	occupancy_measure_date?: string;
	"filter[los]"?: number;
}

export interface PaginationParams {
	page?: number;
	per_page?: number;
}

type ComplexPriceItemsHistoryParams = PaginationParams & {
	sort?: string;
	"filter[active_only]"?: boolean;
};

type MasterPriceList = PaginationParams & {
	sort?: string;
	"filter[administration_id]"?: number;
	"filter[managing_price_list_id]"?: number;
};

type BookingsParams = PaginationParams & {
	sort?: string;
	"filter[administration_id]"?: number;
};

type MinMaxOffsetBreachedParams = PaginationParams & {};

type GetTargetsParams = {
	"filter[week_between]"?: string;
	"filter[rentable_type_id]"?: number;
	"filter[administration_id]"?: number;
	"filter[segment_id]"?: number;
};

type GetTargetYearsParams = {
	administration_id?: number;
};

type GetRentableTypesParams = {
	administration_id?: number;
};

type GetTargetProgressParams = {
	year: number;
	administration_id?: number;
	rentable_type_id?: number;
	segment_id?: number;
};

type ExportTargetsParams = {
	year: number;
	administration_id?: number;
	rentable_type_id?: number;
	segment_id?: number;
};

export interface RentableTypeTargetDO {
	id: number;
	rentable_type_id: number;
	week: string;
	value: string;
	valid_from: string;
	valid_to: string | null;
	year: number;
	weekNum: number;
}

export default class {
	// Master Price List endpoints

	syncGet(
		administration_id: number
	): Promise<Result<MasterPriceComparisonDO, ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/administrations/${administration_id}/sync`)
		);
	}

	syncSet(
		administration_id: number,
		data: MasterPriceComparisonDO
	): Promise<Result<MasterPriceComparisonDO, ApiError>> {
		return wrapper(
			baseClient.post(
				`${basePath}/administrations/${administration_id}/sync`,
				data
			)
		);
	}

	getFailedCurveUpdates(params?: {
		limit: number;
	}): Promise<Result<FailedPriceUpdateDTO[], ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/prices/failed-updates`, { params })
		);
	}

	/**
	 * Set ranges for prices
	 * @param priceListId The Pricelist to apply the ranges to.
	 */
	importPrices(priceListId: number): Promise<Result<MasterPriceDO, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/prices/${priceListId}/import`));
	}

	/**
	 * The import history of a source price list, newest first.
	 */
	getPriceListImports(
		priceListId: number,
		params: { page: number; per_page: number; sort?: string }
	): Promise<
		Result<LaravelPaginatedCollection<MasterPriceListImportListDO>, ApiError>
	> {
		return wrapper(
			baseClient.get(`${basePath}/prices/${priceListId}/imports`, { params })
		);
	}

	getPriceListImport(
		priceListId: number,
		importId: number
	): Promise<Result<MasterPriceListImportDO, ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/prices/${priceListId}/imports/${importId}`)
		);
	}

	/**
	 * Get all master prices
	 */
	getMasterPrices(
		params?: MasterPriceList
	): Promise<Result<MasterPriceDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/prices`, { params }));
	}

	/**
	 * Get a master price
	 */
	getMasterPrice(
		priceListId: number
	): Promise<Result<MasterPriceDO, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/prices/${priceListId}`));
	}

	convertToManaged(
		priceListId: number
	): Promise<Result<MasterPriceDO, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/prices/${priceListId}/manage`));
	}

	/**
	 * Get ranges for a specific master price
	 * @param priceListId The ID of the master price
	 */
	getRangesForPrice(
		priceListId: number,
		los?: number
	): Promise<Result<CurveApplicationRangeDO[], ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/prices/${priceListId}/ranges`, {
				params: { los },
			})
		);
	}

	/**
	 * Set ranges for prices
	 * @param priceListId The Pricelist to apply the ranges to.
	 * @param ranges Array of ranges to set
	 */
	setRanges(
		priceListId: number,
		ranges: CurveApplicationRangesPutDTO
	): Promise<Result<CurveApplicationRangeDO[], ApiError>> {
		return wrapper(
			baseClient.put(`${basePath}/prices/${priceListId}/ranges`, ranges)
		);
	}

	/**
	 * Batch assign prices
	 */
	setBatchRanges(
		priceLists: MasterPriceDO[],
		ranges: CurveApplicationRangesPutDTO
	): Promise<Result<CurveApplicationRangeDO[], ApiError>> {
		return wrapper(
			baseClient.put(`${basePath}/ranges/batch`, {
				priceListIds: priceLists.map((priceList) => priceList.id),
				ranges,
			})
		);
	}

	/**
	 * Copy curves from source pricelist to (multiple) other pricelists
	 * Any active curves in the target pricelist will be removed
	 */
	copyCurves(
		sourceId: number,
		body: CopyCurvesPostDTO
	): Promise<Result<CurveApplicationRangeDO[], ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/prices/${sourceId}/copy-curves`, body)
		);
	}

	/**
	 * Get ranges for a specific master price
	 * @param priceListId The ID of the master price
	 * @param curveId
	 */
	setDefaultCurveForPrice(
		priceListId: number,
		curveId: number
	): Promise<Result<CurveApplicationRangeDO[], ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/prices/${priceListId}/default/${curveId}`)
		);
	}

	/**
	 * Deletes the default curve, resulting in no price changes
	 * @param priceListId The Pricelist to apply the ranges to.
	 */
	deleteDefaultCurveForPrice(
		priceListId: number
	): Promise<Result<CurveApplicationRangeDO[], ApiError>> {
		return wrapper(
			baseClient.delete(`${basePath}/prices/${priceListId}/default`)
		);
	}

	/**
	 * Gets all the applied models that are used inside the pricelist
	 * @param priceListId The Pricelist id
	 */
	getCurvesForPriceList(
		priceListId: number
	): Promise<Result<PriceModelCurveDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/prices/${priceListId}/curves`));
	}

	getRentableTypeDisplayNames(
		rentableTypeIds: number[]
	): Promise<Result<RentableTypeAssetDO[], ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/prices/rentable-types`, {
				params: { rentable_type_ids: rentableTypeIds },
			})
		);
	}

	getCurvesForAllPriceLists(
		priceListIds: number[]
	): Promise<Result<PriceModelCurveDO[], ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/prices/curves`, {
				params: { price_list_ids: priceListIds },
			})
		);
	}

	// Price Model Curve endpoints

	/**
	 * Get all price model curves with optional name filter
	 */
	getPriceModelCurves(
		params?: CurveSearchParams
	): Promise<Result<PriceModelCurveDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/curves`, { params }));
	}

	getRelativeOccupancy(
		priceListId: number,
		params: HistoricCurveSearchParams
	): Promise<Result<number, ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/prices/${priceListId}/relative-occupancy`, {
				params,
			})
		);
	}

	getHistoricPriceModelCurve(
		priceListId: number,
		params?: HistoricCurveSearchParams
	): Promise<Result<ComplexPriceItemCurveApplicationStatsDTO, ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/prices/${priceListId}/history`, { params })
		);
	}

	/**
	 * Create a new price model curve
	 */
	createPriceModelCurve(
		curve: PriceModelCurvePostDTO
	): Promise<Result<PriceModelCurveDO, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/curves`, curve));
	}

	/**
	 * Get a specific price model curve
	 */
	getPriceModelCurve(id: number): Promise<Result<PriceModelCurveDO, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/curves/${id}`));
	}

	/**
	 *
	 * @param curveId
	 * Get all price list that currently use this curve
	 */
	getPriceListForCurve(curveId: number): Promise<Result<any, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/curves/${curveId}/price-lists`));
	}

	/**
	 * Get a specific price model curve
	 */
	getPriceModelCurvePreviousYearData(
		id: number,
		priceListId?: number,
		period?: PeriodSelectionDTO,
		days?: number,
		selectedDayOfWeeks?: number[]
	): Promise<Result<PriceModelCurveBaseDataDTO, ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/curves/${id}/previous`, {
				params: { priceListId, period, days, selectedDayOfWeeks },
			})
		);
	}

	/**
	 * Update a price model curve
	 */
	updatePriceModelCurve(
		id: number,
		curve: PriceModelCurvePatchDTO
	): Promise<Result<PriceModelCurveDO, ApiError>> {
		return wrapper(baseClient.patch(`${basePath}/curves/${id}`, curve));
	}

	getCurveUsages(
		id: number
	): Promise<Result<Record<number, PriceModelCurveUsageDTO[]>, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/curves/${id}/usage`));
	}

	/**
	 * Delete a price model curve
	 */
	deletePriceModelCurve(id: number): Promise<Result<void, ApiError>> {
		return wrapper(baseClient.delete(`${basePath}/curves/${id}`));
	}

	fetchAdministrationsFromBex(
		params: { fetchAll: boolean } = { fetchAll: false }
	): Promise<Result<any, ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/administrations/fetch`, params)
		);
	}

	getAdministrations(): Promise<Result<BexAdministrationDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/administrations`));
	}

	updateEnvironment(
		environment: BexEnvironmentDO
	): Promise<Result<BexEnvironmentDO, ApiError>> {
		return wrapper(baseClient.patch(`${basePath}/environments`, environment));
	}

	getOverallStats(): Promise<Result<OverviewStatsDTO, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/stats`));
	}

	getStatsForPricelist(): Promise<Result<OverviewStatsDTO, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/stats/prices`));
	}

	// To invoke the controller method...
	getRangeStats(
		from: Date,
		to: Date
	): Promise<Result<CurveApplicationRangeImpactDTO[], ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/stats/calendar/range`, {
				params: { from, to },
			})
		);
	}

	getPriceUpdateStats(
		daysToLookBack: number = 7
	): Promise<Result<PriceUpdateStatsDTO, ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/stats/home/price-update-stats`, {
				params: { daysToLookBack },
			})
		);
	}

	getManagedPriceListPricingHistory(
		id: number,
		params: ComplexPriceItemsHistoryParams = {}
	): Promise<
		Result<LaravelPaginatedCollection<ComplexPriceItemDTO>, ApiError>
	> {
		return wrapper(
			baseClient.get(`${basePath}/prices/${id}/pricing-history`, { params })
		);
	}

	getBookings(
		params: BookingsParams = {}
	): Promise<Result<LaravelPaginatedCollection<BookingDTO>, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/bookings`, { params }));
	}

	getMinMaxOffsetBreaches(
		params: MinMaxOffsetBreachedParams = {}
	): Promise<Result<MinMaxOffsetBreachedDTO[], ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/prices/min-max-breaches`, { params })
		);
	}

	getMinMaxOffsetBreachedDates(
		priceListId: number
	): Promise<Result<MinMaxOffsetBreachedDate[], ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/prices/${priceListId}/min-max-breaches-dates`)
		);
	}

	getIsPriceListUpdatingPrices(
		id: number
	): Promise<Result<{ is_updating_prices: boolean }, ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/prices/${id}/is-updating-prices`)
		);
	}

	resetOAuth(): Promise<Result<{}, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/auth/reset`));
	}

	getTargetYears(
		params: GetTargetYearsParams = {}
	): Promise<Result<number[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/targets/years`, { params }));
	}

	getTargets(
		params: GetTargetsParams = {}
	): Promise<Result<RentableTypeTargetDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/targets`, { params }));
	}

	importTargets(file: File): Promise<Result<null, ApiError>> {
		const formData = new FormData();
		formData.append("file", file);
		return wrapper(
			baseClient.post(`${basePath}/targets/import`, formData, {
				headers: { "Content-Type": "multipart/form-data" },
			})
		);
	}

	getRentableTypes(
		params: GetRentableTypesParams = {}
	): Promise<Result<RentableTypeAssetDO[], ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/targets/rentable-types`, { params })
		);
	}

	getRentableSegments(
		params: GetRentableTypesParams = {}
	): Promise<Result<BexRentableSegmentDO[], ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/targets/rentable-segments`, { params })
		);
	}

	getTargetProgress(
		params: GetTargetProgressParams
	): Promise<Result<Record<string, Record<string, number>>, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/targets/progress`, { params }));
	}

	exportTargets(params: ExportTargetsParams): Promise<Result<Blob, ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/targets/export`, {
				params,
				responseType: "blob",
			})
		);
	}
}
